import { getMapGridDimensions, tokenAnchorCell, footprintCenter, getFootprintSquares } from '../../../components/Zombies/utils/pushGeometryCore.mjs';
import { areaBarriers, originReaches } from './spellWalls.mjs';

// Areas of effect on a square grid (D&D 2024): the one rule for server targets and the map preview.
//
// SRD 5.2.1 defines each shape and its point of origin in the Rules Glossary ("Cone", "Cube",
// "Cylinder", "Emanation", "Line", "Sphere"), but its "Playing on a Grid" section gives no area
// rule. The 2024 Dungeon Master's Guide (chapter 2, "Running Combat") does:
//   "choose an intersection of squares ... to be the point of origin"
//   "If an area of effect covers at least half a square ..., the entire square ... is affected."
// A creature is in the area when any square of its space is affected.
//
// Cones follow the DMG's Cone diagram instead: from an intersection, every square the cone's
// template touches is affected, even by a corner, so a 15-foot Cone covers 2, 2 and 4 squares.
// Squares it only meets along an edge or at a point are not, and neither are slivers under a tenth
// of a square, which an aim a little off a grid line would otherwise add (the diagram's smallest is a quarter).
//
// Coordinates are in squares: (0, 0) is the map's top-left intersection, and the square (x, y)
// spans [x, x + 1] × [y, y + 1]. Shapes come from `spellArea` below.

const EPSILON = 1e-8;
// Curved templates are measured on a SAMPLES × SAMPLES lattice of square midpoints; polygons exactly.
export const AREA_SQUARE_SAMPLES = 20;
const CONE_SLIVER = 0.1;

const distanceToSegment = (point, a, b) => {
  const dx = b.x - a.x; const dy = b.y - a.y;
  const lengthSquared = dx * dx + dy * dy;
  const t = lengthSquared ? Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / lengthSquared)) : 0;
  return Math.hypot(point.x - a.x - t * dx, point.y - a.y - t * dy);
};

const rectCorners = (rect) => [{ x: rect.x, y: rect.y }, { x: rect.x + rect.width, y: rect.y },
  { x: rect.x + rect.width, y: rect.y + rect.height }, { x: rect.x, y: rect.y + rect.height }];

export const areaContainsPoint = (area, point) => {
  if (!area) return false;
  if (area.shape === 'cells') return area.cells.some(({ x, y }) => point.x >= x && point.x < x + 1 && point.y >= y && point.y < y + 1);
  if (area.shape === 'emanation') {
    const b = area.bounds;
    if (!area.start) return Math.hypot(Math.max(b.x - point.x, 0, point.x - b.x - b.width), Math.max(b.y - point.y, 0, point.y - b.y - b.height)) <= area.radius + EPSILON;
    // Sweep the caster's occupied rectangle, expanded by the aura radius.
    const corners = [area.start, b].flatMap(rectCorners);
    const sorted = corners.sort((p, q) => p.x - q.x || p.y - q.y);
    const cross = (o, p, q) => (p.x - o.x) * (q.y - o.y) - (p.y - o.y) * (q.x - o.x);
    const half = vertices => { const hull = []; for (const v of vertices) { while (hull.length > 1 && cross(hull.at(-2), hull.at(-1), v) <= 0) hull.pop(); hull.push(v); } return hull; };
    const polygon = [...half(sorted).slice(0, -1), ...half([...sorted].reverse()).slice(0, -1)];
    if (polygon.every((v, i) => cross(v, polygon[(i + 1) % polygon.length], point) >= -EPSILON)) return true;
    return polygon.some((a, i) => distanceToSegment(point, a, polygon[(i + 1) % polygon.length]) <= area.radius + EPSILON);
  }
  if (area.shape === 'composite') return area.shapes.some((shape) => areaContainsPoint(shape, point));
  if (area.shape === 'cube') return point.x >= area.points[0].x - EPSILON && point.x <= area.points[2].x + EPSILON
    && point.y >= area.points[0].y - EPSILON && point.y <= area.points[2].y + EPSILON;
  if (area.shape === 'swept-circle') return distanceToSegment(point, area.start, area.center) <= area.radius + EPSILON;
  if (area.shape === 'circle') return Math.hypot(point.x - area.center.x, point.y - area.center.y) <= area.radius + EPSILON;
  if (!['cone', 'line'].includes(area.shape)) return false;
  const dx = point.x - area.origin.x; const dy = point.y - area.origin.y;
  const along = dx * area.direction.x + dy * area.direction.y;
  const across = Math.abs(dx * area.direction.y - dy * area.direction.x);
  // A Cone's width at any point equals that point's distance from its origin (SRD 5.2.1).
  return along > EPSILON && along <= area.length + EPSILON
    && across <= (area.shape === 'cone' ? along / 2 : area.halfWidth) + EPSILON;
};

// Exact area of a convex polygon clipped to one square (Sutherland–Hodgman).
const clippedPolygonArea = (polygon, cell) => {
  const edges = [
    [(p) => p.x - cell.x, 'x', cell.x], [(p) => cell.x + 1 - p.x, 'x', cell.x + 1],
    [(p) => p.y - cell.y, 'y', cell.y], [(p) => cell.y + 1 - p.y, 'y', cell.y + 1],
  ];
  let vertices = polygon;
  for (const [inside, axis, value] of edges) {
    const next = [];
    for (let index = 0; index < vertices.length; index += 1) {
      const current = vertices[index];
      const previous = vertices[(index + vertices.length - 1) % vertices.length];
      const currentIn = inside(current) >= 0; const previousIn = inside(previous) >= 0;
      if (currentIn !== previousIn) {
        const t = (value - previous[axis]) / (current[axis] - previous[axis]);
        next.push({ x: previous.x + (current.x - previous.x) * t, y: previous.y + (current.y - previous.y) * t });
      }
      if (currentIn) next.push(current);
    }
    vertices = next;
    if (!vertices.length) return 0;
  }
  return Math.abs(vertices.reduce((sum, v, i) => { const w = vertices[(i + 1) % vertices.length]; return sum + v.x * w.y - w.x * v.y; }, 0)) / 2;
};

/** Fraction (0–1) of the square at `cell` that the area's template covers. */
export const squareCoverage = (area, cell) => {
  if (!area || !cell) return 0;
  if (['cube', 'cone', 'line'].includes(area.shape) && Array.isArray(area.points)) return clippedPolygonArea(area.points, cell);
  const gap = (low, high, value) => Math.max(low - value, 0, value - high);
  if (area.shape === 'circle' && Math.hypot(gap(cell.x, cell.x + 1, area.center.x), gap(cell.y, cell.y + 1, area.center.y)) >= area.radius) return 0;
  if (area.shape === 'emanation' && !area.start && Math.hypot(Math.max(0, cell.x - area.bounds.x - area.bounds.width, area.bounds.x - cell.x - 1),
    Math.max(0, cell.y - area.bounds.y - area.bounds.height, area.bounds.y - cell.y - 1)) >= area.radius) return 0;
  const parts = area.shape === 'composite' ? area.shapes : [area];
  const corners = rectCorners({ ...cell, width: 1, height: 1 });
  if (parts.some((part) => corners.every((corner) => areaContainsPoint(part, corner)))) return 1; // every shape is convex
  let inside = 0;
  for (let i = 0; i < AREA_SQUARE_SAMPLES; i += 1) for (let j = 0; j < AREA_SQUARE_SAMPLES; j += 1) {
    if (areaContainsPoint(area, { x: cell.x + (i + 0.5) / AREA_SQUARE_SAMPLES, y: cell.y + (j + 0.5) / AREA_SQUARE_SAMPLES })) inside += 1;
  }
  return inside / (AREA_SQUARE_SAMPLES * AREA_SQUARE_SAMPLES);
};

const areaBounds = (area) => {
  if (!area) return null;
  if (area.shape === 'composite') {
    const parts = area.shapes.map(areaBounds).filter(Boolean);
    return parts.length ? { minX: Math.min(...parts.map((b) => b.minX)), minY: Math.min(...parts.map((b) => b.minY)),
      maxX: Math.max(...parts.map((b) => b.maxX)), maxY: Math.max(...parts.map((b) => b.maxY)) } : null;
  }
  const points = area.shape === 'circle' || area.shape === 'swept-circle'
    ? [area.center, area.start].filter(Boolean).flatMap((c) => [{ x: c.x - area.radius, y: c.y - area.radius }, { x: c.x + area.radius, y: c.y + area.radius }])
    : area.shape === 'emanation'
      ? [area.bounds, area.start].filter(Boolean).flatMap((b) => [{ x: b.x - area.radius, y: b.y - area.radius }, { x: b.x + b.width + area.radius, y: b.y + b.height + area.radius }])
      : area.points || [];
  if (!points.length) return null;
  return { minX: Math.min(...points.map((p) => p.x)), minY: Math.min(...points.map((p) => p.y)),
    maxX: Math.max(...points.map((p) => p.x)), maxY: Math.max(...points.map((p) => p.y)) };
};

const affected = (area, cell) => {
  if (area.shape === 'cone') return squareCoverage(area, cell) > CONE_SLIVER;
  if (area.shape !== 'composite') return squareCoverage(area, cell) >= 0.5 - EPSILON;
  // Several areas count once: the union must cover half. Skip sampling the union when one part
  // already covers half, or when all parts together cannot.
  const parts = area.shapes.map((part) => squareCoverage(part, cell));
  if (Math.max(...parts) >= 0.5 - EPSILON) return true;
  if (parts.reduce((sum, coverage) => sum + coverage, 0) < 0.5 - EPSILON) return false;
  return squareCoverage(area, cell) >= 0.5 - EPSILON;
};

/** Squares the area affects: at least half covered (any part, for a Cone), limited to the map. Sorted by row, then column. */
export const areaSquares = (area, { columns, rows }) => {
  if (area?.shape === 'cells') return area.cells;
  const bounds = areaBounds(area);
  if (!bounds) return [];
  const squares = [];
  const top = Math.max(0, Math.floor(bounds.minY + EPSILON)); const bottom = Math.min(rows, Math.ceil(bounds.maxY - EPSILON));
  const left = Math.max(0, Math.floor(bounds.minX + EPSILON)); const right = Math.min(columns, Math.ceil(bounds.maxX - EPSILON));
  for (let y = top; y < bottom; y += 1) for (let x = left; x < right; x += 1) {
    if (affected(area, { x, y })) squares.push({ x, y });
  }
  return squares;
};

/**
 * Point of origin for an area that starts at a creature (Cone, Line, Cube from self): the grid
 * point on the perimeter of the creature's space nearest where its aim leaves that space.
 * Cones use intersections, as the DMG says. Lines may also start at the midpoint of a square's
 * edge: from an intersection, a 5-foot Line along a grid line would cover exactly half of two
 * rows, and the half-square rule would then affect both.
 */
export const perimeterOrigin = ({ anchor, size, direction, edgeMidpoints = false }) => {
  const center = { x: anchor.x + size / 2, y: anchor.y + size / 2 };
  const reach = Math.min(...['x', 'y'].map((axis) => (Math.abs(direction[axis]) < EPSILON ? Infinity : (size / 2) / Math.abs(direction[axis]))));
  const exit = { x: center.x + direction.x * reach, y: center.y + direction.y * reach };
  const step = edgeMidpoints ? 0.5 : 1;
  const candidates = [];
  for (let offset = 0; offset <= size + EPSILON; offset += step) {
    candidates.push({ x: anchor.x + offset, y: anchor.y }, { x: anchor.x + offset, y: anchor.y + size },
      { x: anchor.x, y: anchor.y + offset }, { x: anchor.x + size, y: anchor.y + offset });
  }
  // Ties (aim through the middle of an edge with only corners allowed) go to the earlier candidate.
  return candidates.reduce((best, candidate) => (Math.hypot(candidate.x - exit.x, candidate.y - exit.y)
    < Math.hypot(best.x - exit.x, best.y - exit.y) - EPSILON ? candidate : best));
};

// Map areas use five-foot squares and affect every creature, allies included, with a square in
// the area (`areaSquares`). Origins snap to the grid. This is a two-dimensional footprint;
// Elevation and cover short of Total remain DM rulings. Total Cover from the point of origin stops an area (mapAreaSquares).
export const spellArea = ({ area, point, sourceToken, sourceSize, map }) => {
  const { columns, rows } = getMapGridDimensions(map);
  if (!point || ![point.x, point.y].every((v) => Number.isFinite(v) && v >= 0 && v <= 1)) return null;
  if (area?.shape === 'cells') return { shape: 'cells', cells: area.cells };
  const cursor = { x: point.x * columns, y: point.y * rows };
  if (area?.shape === 'emanation') {
    // An Emanation extends from its creature's space, which sits on whole squares.
    const size = Math.max(1, area.footprintSquares || 1);
    const x = Math.round(cursor.x); const y = Math.round(cursor.y);
    return { shape: 'emanation', excludeSource: true, center: { x: x + size / 2, y: y + size / 2 },
      bounds: { x, y, width: size, height: size }, radius: area.radiusFeet / 5 };
  }
  // A Sphere's or Cylinder's origin is its center: the grid intersection nearest the cursor.
  if (['sphere', 'cylinder'].includes(area?.shape)) return { shape: 'circle', center: { x: Math.round(cursor.x), y: Math.round(cursor.y) }, radius: area.radiusFeet / 5 };
  if (area?.shape === 'cube' && area.origin === 'self') {
    // "A point of origin located anywhere on a face of the Cube": the cube sits against the side
    // of the caster's space faced by the cursor, aligned to the grid, sliding along that side.
    const anchor = tokenAnchorCell(sourceToken, map);
    if (!anchor) return null;
    const size = getFootprintSquares(sourceSize);
    const origin = footprintCenter(anchor, sourceSize);
    const dx = cursor.x - origin.x; const dy = cursor.y - origin.y;
    if (Math.hypot(dx, dy) < .001) return null;
    const side = area.sizeFeet / 5;
    const horizontal = Math.abs(dx) >= Math.abs(dy);
    const direction = horizontal ? Math.sign(dx) : Math.sign(dy);
    const slide = (start, aim) => Math.min(start + size, Math.max(start - side, Math.round(aim - side / 2)));
    const left = horizontal ? (direction < 0 ? anchor.x - side : anchor.x + size) : slide(anchor.x, cursor.x);
    const top = horizontal ? slide(anchor.y, cursor.y) : (direction < 0 ? anchor.y - side : anchor.y + size);
    return { shape: 'cube', excludeSource: true, center: { x: left + side / 2, y: top + side / 2 },
      points: [{ x: left, y: top }, { x: left + side, y: top }, { x: left + side, y: top + side }, { x: left, y: top + side }] };
  }
  if (area?.shape === 'cube') {
    const side = area.sizeFeet / 5;
    const left = Math.floor(cursor.x); const top = Math.floor(cursor.y);
    return { shape: 'cube', center: { x: left + side / 2, y: top + side / 2 },
      points: [{ x: left, y: top }, { x: left + side, y: top }, { x: left + side, y: top + side }, { x: left, y: top + side }] };
  }
  const anchor = tokenAnchorCell(sourceToken, map);
  if (!anchor || !['cone', 'line'].includes(area?.shape)) return null;
  // The cursor sets the aim from the caster's center; the origin is the perimeter grid point
  // where that aim leaves the caster's space.
  const center = footprintCenter(anchor, sourceSize);
  const dx = cursor.x - center.x; const dy = cursor.y - center.y;
  const distance = Math.hypot(dx, dy);
  if (distance < 0.001) return null;
  const direction = { x: dx / distance, y: dy / distance };
  const origin = perimeterOrigin({ anchor, size: getFootprintSquares(sourceSize), direction, edgeMidpoints: area.shape === 'line' });
  const length = (area.shape === 'cone' ? area.sizeFeet : area.lengthFeet) / 5;
  const halfWidth = (area.shape === 'cone' ? area.sizeFeet : area.widthFeet) / 10;
  const end = { x: origin.x + direction.x * length, y: origin.y + direction.y * length };
  const offset = (p, width) => ({ x: p.x - direction.y * width, y: p.y + direction.x * width });
  return { shape: area.shape, origin, direction, length, halfWidth,
    points: area.shape === 'cone' ? [origin, offset(end, halfWidth), offset(end, -halfWidth)]
      : [offset(origin, halfWidth), offset(end, halfWidth), offset(end, -halfWidth), offset(origin, -halfWidth)] };
};

/** The squares the area affects on this map (shared by target selection and the map preview). */
export const spellAreaSquares = ({ geometry, map }) => areaSquares(geometry, getMapGridDimensions(map));

/** The squares spells have made Difficult Terrain on this map: lasting areas (Web, Insect Plague) and Ice Storm's ground. */
export const spellTerrainSquares = (effects, map) => (effects || [])
  .filter((effect) => effect?.terrain?.difficultTerrain && effect.placement?.mapId === map?.mapId)
  .flatMap((effect) => {
    const geometry = spellArea({ area: effect.area, point: effect.placement.point, map });
    return geometry ? spellAreaSquares({ geometry, map }) : [];
  });

/** Ground a spell leaves behind after it resolves, until the end of the caster's next turn. */
export const createTerrainAreaEffect = ({ definitionId, name, area, placement, sourceCombatantId, resolutionId, turnSequence }) => ({
  id: `spell-terrain:${resolutionId}`, definitionId: `${definitionId}:terrain`, kind: 'terrain-area', name, userVisible: false,
  sourceCombatantId, targetCombatantId: sourceCombatantId, sourceResolutionId: resolutionId, stackKey: `spell-terrain:${resolutionId}`, stackPolicy: 'refresh',
  appliedAtTurnSequence: turnSequence, removeOnSourceRemoval: false, area, placement, terrain: { difficultTerrain: true },
  expiration: { type: 'sourceTurn', combatantId: sourceCombatantId, boundary: 'end', remainingOccurrences: 1 },
  duration: 'Until the end of the caster’s next turn', description: 'Difficult Terrain.',
});

/** What stops areas on this map right now: solid spell walls and the map's own walls. */
export const mapAreaBarriers = (effects, map) => areaBarriers({ effects, map, ...getMapGridDimensions(map) });
/** The affected squares, less those with Total Cover from the area's point of origin. */
export const mapAreaSquares = ({ geometry, map, barriers }) => {
  if (!barriers?.edges?.length && !barriers?.cells?.length) return spellAreaSquares({ geometry, map });
  return (geometry?.shape === 'composite' ? geometry.shapes : [geometry]).flatMap((shape) => {
    const origin = shape?.origin || shape?.center;
    const squares = spellAreaSquares({ geometry: shape, map });
    return origin ? squares.filter(({ x, y }) => originReaches(origin, { x: x + .5, y: y + .5 }, barriers)) : squares;
  });
};

export const areaTargetIds = ({ geometry, tokens, map, sourceCombatantId, allowZeroHp = false, barriers }) => {
  const squares = new Set(mapAreaSquares({ geometry, map, barriers }).map((square) => `${square.x},${square.y}`));
  return tokens.filter((token) => {
    if ((geometry?.excludeSource || ['cone', 'line'].includes(geometry?.shape) || (geometry?.shape === 'composite' && geometry.shapes.every((shape) => shape.excludeSource || ['cone', 'line'].includes(shape.shape)))) && token.characterId === sourceCombatantId) return false;
    const anchor = tokenAnchorCell(token, map);
    if (!anchor || (!allowZeroHp && !(Number(token.currentHp) > 0))) return false;
    // A creature is affected when any square of its space is in the area.
    const size = getFootprintSquares(token.size);
    for (let x = 0; x < size; x += 1) for (let y = 0; y < size; y += 1) {
      if (squares.has(`${anchor.x + x},${anchor.y + y}`)) return true;
    }
    return false;
  }).map((token) => token.characterId).sort();
};

export const buildSpellAreaPlacement = ({ area, placement, sourceToken, sourceSize, map }) => {
  const points = placement?.points || (placement?.point ? [placement.point] : []);
  const minimum = area.minAreas || 1; const maximum = area.maxAreas || 1;
  if (!Array.isArray(points) || points.length < minimum || points.length > maximum) throw new Error(`Choose ${minimum === maximum ? minimum : `${minimum}–${maximum}`} areas.`);
  if (new Set(points.map((point) => JSON.stringify(point))).size !== points.length) throw new Error('Choose distinct area locations.');
  const shapes = points.map((point) => spellArea({ area, point, sourceToken, sourceSize, map }));
  if (shapes.some((shape) => !shape)) throw new Error('Choose a valid area position or direction.');
  if (new Set(shapes.map((shape) => JSON.stringify(shape))).size !== shapes.length) throw new Error('Choose distinct area locations.');
  const sourceAnchor = tokenAnchorCell(sourceToken, map);
  if (!sourceAnchor) throw new Error('Place the caster on the active map first.');
  const origin = area.rangeOriginPoint ? { x: area.rangeOriginPoint.x * getMapGridDimensions(map).columns, y: area.rangeOriginPoint.y * getMapGridDimensions(map).rows } : footprintCenter(sourceAnchor, sourceSize);
  if (Number.isFinite(area.rangeFeet) && shapes.some((shape) => Math.hypot(shape.center.x - origin.x, shape.center.y - origin.y) * 5 > area.rangeFeet)) throw new Error('An area is outside the spell’s range.');
  if (area.requiresNeighbor && shapes.some((shape, index) => !shapes.some((other, otherIndex) => {
    if (index === otherIndex) return false;
    const gapX = Math.abs(shape.center.x - other.center.x); const gapY = Math.abs(shape.center.y - other.center.y);
    const side = area.sizeFeet / 5;
    return (Math.abs(gapX - side) < 1e-8 && gapY < side - 1e-8) || (Math.abs(gapY - side) < 1e-8 && gapX < side - 1e-8);
  }))) throw new Error('Each Fire Storm cube must share an edge with another cube.');
  return shapes.length === 1 ? shapes[0] : { shape: 'composite', shapes };
};
